import { Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "@softmax/web-features/navigation";
import { VerifiedViewer } from "@/session/product-session";
import { useGameContext } from "../frame/use-game-context";
import { GamePending } from "../frame/game-pending";
import { gameIndexQueryOptions } from "../game.queries";
import { gameIndexHead } from "./game-index-head";
import { GameMetadata } from "./game-metadata";
import { GameIndexContent } from "./game-index-content";

export function GameIndex() {
  const game = useGameContext();
  const options = gameIndexQueryOptions(game.publicPath);
  const query = useQuery(options);
  const page = query.data;
  const parameters = useSearchParams();

  if (!page) {
    if (query.error) throw query.error;

    return <GamePending />;
  }

  const viewer =
    page.kind === "home"
      ? page.data.content.viewer
      : page.kind === "theater"
        ? page.data.viewer
        : page.game.viewer;
  const result = (
    <>
      <GameMetadata head={gameIndexHead(page)} />
      <RefreshNotice error={query.error} retry={query.refetch} />
      <Suspense fallback={<GamePending />}>
        <GameIndexContent
          page={page}
          updatedAt={query.dataUpdatedAt}
          parameters={parameters}
        />
      </Suspense>
    </>
  );
  return viewer ? (
    <VerifiedViewer viewer={viewer} fallback={<GamePending />}>
      {result}
    </VerifiedViewer>
  ) : (
    result
  );
}

function RefreshNotice({
  error,
  retry,
}: {
  readonly error: unknown;
  readonly retry: () => Promise<unknown>;
}) {
  if (!error) return null;

  return (
    <output className="block px-6 py-2 text-sm">
      Game data could not be refreshed.{" "}
      <button type="button" onClick={() => void retry()}>
        Try again
      </button>
    </output>
  );
}
